import { Button, Dialog, DialogBackdrop, DialogPanel, DialogTitle } from '@headlessui/react';
import {
  UserCircleIcon,
} from '@heroicons/react/24/outline';
import { createUserWithEmailAndPassword, signInWithEmailAndPassword, GoogleAuthProvider, signInWithPopup, signInWithRedirect, sendEmailVerification } from "firebase/auth";
import { doc, setDoc, getDoc } from "firebase/firestore";
import PropTypes from 'prop-types';
import { useState } from 'react';
import { toast } from 'react-hot-toast';
import { auth, db } from "../firebase";
import { FormField } from './FormField';

const USER_ACTIONS = {
  login: {
    title: "Login",
    topBtnTxt: "Create New Account",
    submitBtnTxt: "Login",
    action: signInWithEmailAndPassword,
    autoComplete: "current-password",
  },
  createAccount: {
    title: "Create Account",
    topBtnTxt: "Already a user? Login",
    submitBtnTxt: "Create Account",
    action: createUserWithEmailAndPassword,
    autoComplete: "new-password",
  }
};

export const LoginDialog = ({ isMenuOpen, setMenuOpen }) => {
  const [inputEmail, setInputEmail] = useState("");
  const [inputPassword, setInputPassword] = useState("");
  const [inputName, setInputName] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [currentAction, setCurrentAction] = useState("login");

  const currentUserAction = USER_ACTIONS[currentAction];

  const toggleUserAction = () => {
    setCurrentAction(currentAction === "login" ? "createAccount" : "login");
    setInputEmail("");
    setInputPassword("");
    setInputName("");
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    if (!inputEmail || !inputPassword) return;
    if (currentAction === "createAccount" && !inputName) {
      toast.error('Please enter your name');
      return;
    }

    setIsLoading(true);
    try {
      const userCredential = await currentUserAction.action(auth, inputEmail, inputPassword);

      if (currentAction === "createAccount") {
        const user = userCredential.user;

        // Create user document in Firestore
        await setDoc(doc(db, "users", user.uid), {
          userId: user.uid,
          email: user.email,
          displayName: inputName,
          emailVerified: false,
          createdAt: new Date().toISOString()
        });

        // Send verification email with redirect URL
        try {
          await sendEmailVerification(user, {
            url: `${window.location.origin}/email-verified`,
            handleCodeInApp: false
          });

          toast.success('Account created! Please check your email to verify.', {
            duration: 6000,
          });
        } catch (verifyError) {
          console.error('Error sending verification email:', verifyError);
          toast.error('Account created but verification email failed. You can resend it from the verification screen.');
        }

        setMenuOpen(false);
        setInputEmail("");
        setInputPassword("");
        setInputName("");
      } else {
        // Login successful
        toast.success('Logged in successfully!', {
          icon: <UserCircleIcon className="w-4 h-4" />,
        });
        setMenuOpen(false);
        setInputEmail("");
        setInputPassword("");
      }
    } catch (error) {
      console.error(`${currentAction} error:`, error);
      toast.error(getErrorMessage(error.code));
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setIsLoading(true);

    // Detect mobile before try/catch so it's accessible in error handling
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

    try {
      const provider = new GoogleAuthProvider();

      // Try popup first for all devices (works when called directly from user click)
      const result = await signInWithPopup(auth, provider);
      const user = result.user;

      // Check if user document exists in Firestore, create if not
      const userDocRef = doc(db, "users", user.uid);
      const userDoc = await getDoc(userDocRef);

      if (!userDoc.exists()) {
        await setDoc(userDocRef, {
          userId: user.uid,
          email: user.email,
          displayName: user.displayName || user.email.split('@')[0],
          photoURL: user.photoURL || null,
          emailVerified: user.emailVerified,
          createdAt: new Date().toISOString()
        });
      }

      toast.success('Signed in with Google!', {
        icon: <UserCircleIcon className="w-4 h-4" />,
      });

      setMenuOpen(false);
      setIsLoading(false);
    } catch (error) {
      console.error('Google sign-in error:', error);

      // Handle specific error cases
      if (error.code === 'auth/popup-blocked' && isMobile) {
        // Fallback to redirect on mobile if popup is blocked
        try {
          await signInWithRedirect(auth, new GoogleAuthProvider());
          // Don't set loading to false - page will redirect
          // The result will be handled by AuthContext on return
          return;
        } catch (redirectError) {
          console.error('Redirect fallback error:', redirectError);
          setIsLoading(false);
          toast.error('Sign-in failed. Please try again.', {
            duration: 5000,
          });
        }
      } else if (error.code === 'auth/popup-blocked') {
        setIsLoading(false);
        toast.error('Popup was blocked. Please allow popups for this site and try again.', {
          duration: 5000,
        });
      } else if (error.code === 'auth/popup-closed-by-user') {
        // User closed popup, don't show error
        setIsLoading(false);
        return;
      } else if (error.code === 'auth/cancelled-popup-request') {
        // Multiple popup requests, ignore
        setIsLoading(false);
        return;
      } else {
        setIsLoading(false);
        toast.error(getErrorMessage(error.code));
      }
    }
  };

  const getErrorMessage = (errorCode) => {
    const errorMessages = {
      'auth/invalid-email': 'Invalid email address',
      'auth/user-disabled': 'This account has been disabled',
      'auth/user-not-found': 'No account found with this email',
      'auth/wrong-password': 'Incorrect password',
      'auth/invalid-credential': 'Invalid email or password',
      'auth/email-already-in-use': 'This email is already registered. Please login instead.',
      'auth/weak-password': 'Password should be at least 6 characters',
      'auth/popup-closed-by-user': 'Sign-in cancelled',
      'auth/popup-blocked': 'Popup blocked. Please allow popups for this site.',
      'auth/account-exists-with-different-credential': 'Account already exists with a different sign-in method',
    };
    return errorMessages[errorCode] || 'An error occurred. Please try again.';
  };

  return (
    <Dialog open={isMenuOpen} onClose={() => setMenuOpen(false)} className="relative z-50 text-white">
      <DialogBackdrop className="fixed inset-0 bg-black/70" />
      <div className="fixed inset-0 flex w-screen items-center justify-center p-1 sm:p-4">
        <DialogPanel className="border border-white/20 w-full max-w-[700px] rounded-[50px] bg-[#4A4A4A]/10 backdrop-blur-sm p-8 duration-300 ease-out data-[closed]:transform-[scale(95%)] data-[closed]:opacity-0">
          <DialogTitle className="font-bold text-xl mb-3 flex items-center gap-2">{currentUserAction.icon} {currentUserAction.title}</DialogTitle>
          <Button
            type="button"
            className="bg-transparent border-b border-[#6bbdaa]/50 text-sm font-medium text-white mb-8 focus:outline-none transition-colors disabled:opacity-50"
            onClick={toggleUserAction}
            disabled={isLoading}>
            {currentUserAction.topBtnTxt}
          </Button>

          <form onSubmit={handleFormSubmit} className="space-y-4">
            {currentAction === "createAccount" && (
              <FormField
                id="name"
                label="Name"
                type="text"
                value={inputName}
                placeholder="Enter your name"
                autoComplete="name"
                onChange={(e) => setInputName(e.target.value)}
              />
            )}
            <FormField
              id="email"
              label="Email"
              type="email"
              value={inputEmail}
              placeholder="Enter email"
              autoComplete="email"
              onChange={(e) => setInputEmail(e.target.value)}
            />
            <FormField
              id="password"
              label="Password"
              type="password"
              value={inputPassword}
              placeholder="Enter password"
              autoComplete={currentUserAction.autoComplete}
              onChange={(e) => setInputPassword(e.target.value)}
            />

            <Button
              type="submit"
              disabled={isLoading}
              className="w-44 py-2 bg-[#7BCDBA] text-sm text-black rounded-full hover:bg-[#6bbdaa] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 justify-center">
              {isLoading ? 'Please wait...' : currentUserAction.submitBtnTxt}
            </Button>
          </form>

          <div className="flex items-center gap-4 my-6">
            <div className="flex-1 border-t border-white/20"></div>
            <span className="text-sm text-white/60">OR</span>
            <div className="flex-1 border-t border-white/20"></div>
          </div>

          <Button
            type="button"
            disabled={isLoading}
            onClick={handleGoogleSignIn}
            className="w-full py-2 bg-white text-sm text-gray-700 rounded-full hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 justify-center font-medium">
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            {isLoading ? 'Please wait...' : 'Continue with Google'}
          </Button>
        </DialogPanel>
      </div>
    </Dialog>
  );
};

LoginDialog.propTypes = {
  isMenuOpen: PropTypes.bool.isRequired,
  setMenuOpen: PropTypes.func.isRequired,
};
